import React, { useEffect, useRef, useState } from "react";
import { Button, Col, FloatingLabel, Form, Modal, Row } from "react-bootstrap";
import InputText from "../../input/inputText";
import axios from "axios";
import HrText from "../../input/hrText";
import { MapWrapper, customIcon } from "../../../page/SecondScreen/mapPage";
import { MapContainer, TileLayer, Marker, useMapEvent } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useDispatch } from "react-redux";
import {
  addLocationPoint,
  editLocationPoint,
} from "../../../redux/slices/settings";
import { useSelector } from "react-redux";

export default function ModalAddLocation({ handleClose, editPointId }) {
  const { points, allCountries, rule, lang } = useSelector((state) => ({
    points: state.settings.location.points,
    allCountries: state.settings.location.countries,
    rule: state.settings.location.rule,
    lang: state.changeLang.language.restriction,
  }));

  const mapRef = useRef(null);
  const dispatch = useDispatch();

  const [allCities, setAllCities] = useState([]);
  const [countryId, setCountryId] = useState(null);
  const [cityId, setCityId] = useState(null);
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [range, setRange] = useState(0);

  const isDisabled = !countryId && (!latitude || !longitude);

  useEffect(() => {
    let mounted = true;
    if (countryId) {
      try {
        axios(`/restrictions/location/get-cities?country=${countryId}`).then(
          ({ data }) => {
            mounted &&
              setAllCities(
                Object.entries(data)
                  .map((item) => {
                    return { id: item[1].id, city: item[1].title };
                  })
                  .sort((a, b) => a.city.localeCompare(b.city))
              );
          }
        );
      } catch (err) {}
    }
    return () => {
      mounted = false;
    };
  }, [countryId]);

  useEffect(() => {
    if (editPointId) {
      const pointData = points.find((item) => item.id === editPointId);
      if (!pointData) {
        return;
      }
      setCountryId(pointData.countryId);
      setCityId(pointData.cityId);
      setLatitude(pointData.lat);
      setLongitude(pointData.long);
      setRange(pointData.range);
      setTimeout(() => {
        if (mapRef.current && pointData.lat && pointData.long) {
          mapRef.current.setView(
            [pointData.lat, pointData.long],
            mapRef.current.getZoom(),
            {
              animate: true,
            }
          );
        }
      });
    }
  }, [editPointId]);

  const handleLatitude = (e) => {
    let str = e.target.value;
    if (/^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/gi.test(str) || !str) {
      setLatitude(Number(str));
      resetCountryCity();
    }
  };

  const handleLongitude = (e) => {
    const str = e.target.value;
    if (
      /^[-+]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/gi.test(str) ||
      !str
    ) {
      setLongitude(Number(str));
      resetCountryCity();
    }
  };

  const flyToMarker = () => {
    if (longitude && latitude) {
      mapRef.current.setView([latitude, longitude], mapRef.current.getZoom(), {
        animate: true,
      });
    }
  };

  const handleAddLocation = () => {
    const point = {
      id: editPointId || `${Date.now()}generator`,
      lat: latitude,
      long: longitude,
      range,
      cityId,
      countryId,
      rule,
    };
    if (editPointId) {
      dispatch(editLocationPoint(point));
    } else {
      dispatch(addLocationPoint(point));
    }
    handleClose();
  };

  const resetCountryCity = () => {
    setCountryId(null);
    setCityId(null);
  };

  const resetLatLong = () => {
    setLatitude(null);
    setLongitude(null);
    setRange(0);
  };

  const LocationMarker = () => {
    const map = useMapEvent("click", (e) => {
      setLatitude(e.latlng.lat);
      setLongitude(e.latlng.lng);
      resetCountryCity();
      map.setView(e.latlng, map.getZoom(), {
        animate: true,
      });
    });

    return !latitude && !longitude ? null : (
      <Marker position={[latitude, longitude]} icon={customIcon} />
    );
  };

  return (
    <Modal show={true} centered onHide={handleClose} scrollable>
      <Modal.Header closeButton className="border-0">
        <Modal.Title className="text-capitalize">{lang[10]}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pb-0">
        <FloatingLabel label={lang[12]} className="mb-3">
          <Form.Select
            value={countryId || ""}
            onChange={(e) => {
              setCountryId(Number(e.target.value));
              resetLatLong();
            }}
          >
            <option disabled value="">
              {lang[13]}
            </option>
            {allCountries.map((item) => (
              <option key={item.id} value={item.id}>
                {item.country}
              </option>
            ))}
          </Form.Select>
        </FloatingLabel>

        <FloatingLabel label={lang[14]}>
          <Form.Select
            value={cityId || ""}
            onChange={(e) => {
              setCityId(Number(e.target.value));
              resetLatLong();
            }}
          >
            <option value={""}>{lang[15]}</option>
            {allCities.map((item) => (
              <option key={item.id} value={item.id}>
                {item.city}
              </option>
            ))}
          </Form.Select>
        </FloatingLabel>

        <HrText text={lang[16]} fullWidth />
        <Row className="gx-3">
          <Col xs={6}>
            <InputText
              type="number"
              min={-90}
              max={90}
              step={0.1}
              name="latitude"
              value={latitude || ""}
              onKeyDown={(e) => e.keyCode === 69 && e.preventDefault()}
              onChange={handleLatitude}
              placeholder={lang[17]}
              onBlur={flyToMarker}
              popoverText={null}
            />
          </Col>
          <Col xs={6}>
            <InputText
              type="number"
              name="longitude"
              value={longitude || ""}
              onKeyDown={(e) => e.keyCode === 69 && e.preventDefault()}
              onChange={handleLongitude}
              placeholder={lang[18]}
              onBlur={flyToMarker}
              popoverText={null}
            />
          </Col>
        </Row>

        <InputText
          placeholder={lang[19]}
          type="number"
          name="range"
          value={range}
          min="0"
          max="10000"
          step="1"
          onKeyDown={(e) =>
            ["e", "E", "+", "-", "."].includes(e.key) && e.preventDefault()
          }
          onChange={(e) =>
            setRange(e.target.value ? Number(e.target.value) : null)
          }
          popoverText={null}
        />

        <MapWrapper>
          <MapContainer
            center={[40.6699, -103.5917]}
            zoom={3}
            scrollWheelZoom={false}
            className="w-100"
            style={{ height: 220 }}
            ref={mapRef}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <LocationMarker />
          </MapContainer>
        </MapWrapper>
      </Modal.Body>
      <Modal.Footer className="d-block border-0">
        <Row className="g-2">
          <Col xs={6}>
            <Button
              variant="outline-primary"
              className="w-100 py-3 h-100"
              onClick={handleClose}
            >
              {lang[20]}
            </Button>
          </Col>
          <Col xs={6}>
            <Button
              className="w-100 py-3 h-100"
              onClick={handleAddLocation}
              disabled={isDisabled}
            >
              {editPointId ? lang[21] : lang[10]}
            </Button>
          </Col>
        </Row>
      </Modal.Footer>
    </Modal>
  );
}
